import axios from "axios";
import {clearAuthSession} from "./auth-session";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * Keeps the access token fresh.
 *
 * The access token lives 5 minutes (SecurityConstant.ACCESS_TOKEN_TTL). Until now it was only ever
 * renewed REACTIVELY, when a REST call came back 401 — but the STOMP socket authenticates with the
 * same token on CONNECT. So an operator who simply read a conversation for five minutes lost their
 * socket, and every 3-second reconnect re-sent the SAME expired token and was rejected again, for as
 * long as the page stayed open. Realtime only came back on a full page reload, which is exactly the
 * "I have to refresh to see new messages" symptom.
 *
 * Renewing on a timer keeps localStorage valid for BOTH transports, so a reconnect authenticates
 * on its own.
 *
 * <p><b>And when renewal itself fails, the session must end loudly.</b> This is the second half of
 * the same story, and it cost an operator a whole shift: once their refresh token was genuinely
 * dead, every renewal answered 401, the failure was swallowed as if it were a network blip, and the
 * console sat on "آفلاین — در حال اتصال…" for hours. Nothing signed them out, because the operator
 * console is almost entirely STOMP — it makes no REST calls while you watch a conversation, so the
 * axios interceptor's logout path never ran. They believed they were online and were receiving
 * nothing. A dead session has to look dead.
 */

const REFRESH_EVERY_MS = 210_000; // 3.5 min — comfortably inside the 5-minute access-token TTL

let inFlight = null;
let timer = null;
let sessionEnded = false;

/** Fired on `window` when renewal fails terminally, so React views can re-render. */
export const SESSION_ENDED_EVENT = "npe:session-ended";

/**
 * The session is over and cannot be recovered here. Clear it and send them to sign in.
 *
 * <p>Guarded so that a burst of simultaneous failures (the renewal timer and a STOMP error landing
 * together) produces one redirect, not several.
 */
function endSession(reason) {
    if (sessionEnded) return;
    sessionEnded = true;
    if (timer) {
        clearInterval(timer);
        timer = null;
    }
    console.warn("[auth] session ended:", reason);
    // Everything, from both stores. Leaving the role or the old per-tab copy behind is how a dead
    // session came back to life on the very next reload.
    clearAuthSession();
    // Tell the UI, so anything showing a connection state can stop claiming to be connecting.
    // isSessionEnded() is plain module state that React cannot observe on its own.
    try {
        window.dispatchEvent(new Event(SESSION_ENDED_EVENT));
    } catch (e) {
        // very old browsers only — the redirect below is the real remedy
    }
    if (!window.location.pathname.startsWith("/login")) {
        // Carried so the login screen can explain why they are there instead of just appearing.
        window.location.href = "/login?reason=session-expired";
    }
    // If we are ALREADY on /login there is no navigation, so nothing would ever clear the flag or
    // restart the timer — see beginSession, which the login screen calls to undo exactly that.
}

/**
 * Re-arms renewal after a successful sign-in.
 *
 * <p>Needed because ending a session is otherwise a one-way latch. When the failure happens while the
 * tab is already on /login there is no page navigation to reset module state, so without this the
 * NEXT login in that same tab would run with `sessionEnded` still true and the renewal timer still
 * cleared — a freshly signed-in operator whose token would never be renewed, which is the very
 * failure this file exists to prevent.
 */
export function beginSession() {
    sessionEnded = false;
    startTokenRefreshLoop();
}

/**
 * Renews the access token. Concurrent callers share one request. Resolves null on failure.
 *
 * <p>Distinguishes the two failure kinds that used to be treated identically:
 * <ul>
 *   <li><b>terminal</b> (401/403) — the refresh token is dead. No amount of retrying fixes it, so
 *       the session ends and the operator is sent to sign in.</li>
 *   <li><b>transient</b> (offline, timeout, 5xx) — retrying is exactly right. Stay signed in and
 *       let the next tick try again.</li>
 * </ul>
 */
export function refreshAccessToken() {
    if (sessionEnded) return Promise.resolve(null);
    const refreshToken = localStorage.getItem("refresher-token");
    if (!refreshToken) return Promise.resolve(null); // logged out — nothing to do
    if (inFlight) return inFlight;

    inFlight = axios
        .post(`${BASE_URL}/refresh`, {refreshToken: `Bearer ${refreshToken}`})
        .then((res) => {
            const token = res?.data?.accessToken;
            if (token) localStorage.setItem("access-token", token);
            // The backend now slides the refresh token as well, so an operator working a long shift
            // is not cut off mid-conversation. Storing it is what makes the session slide; dropping
            // it would silently reinstate the fixed 8-hour ceiling that caused the incident.
            const rotated = res?.data?.refreshToken;
            if (rotated) localStorage.setItem("refresher-token", rotated);
            return token || null;
        })
        .catch((err) => {
            const status = err?.response?.status;
            if (status === 401 || status === 403) {
                endSession(`refresh rejected with ${status}`);
            }
            // Anything else — no response at all, a 5xx, a timeout — is transient. Keep the session
            // and let the next tick retry.
            return null;
        })
        .finally(() => {
            inFlight = null;
        });

    return inFlight;
}

/**
 * Starts the renewal loop. Also renews when the tab becomes visible again: a laptop that slept
 * through the interval wakes with an expired token and would otherwise reconnect into a rejection.
 */
export function startTokenRefreshLoop() {
    if (timer) return;
    timer = setInterval(refreshAccessToken, REFRESH_EVERY_MS);
    document.addEventListener("visibilitychange", () => {
        if (!document.hidden) refreshAccessToken();
    });
    window.addEventListener("online", refreshAccessToken);
}

/** True once renewal has failed terminally — used to stop showing "connecting…" over a dead session. */
export function isSessionEnded() {
    return sessionEnded;
}
